import type { UsersTranslations } from "../interfaces/users";

export const usersTranslations: UsersTranslations = {
  page_title: "Users",
  page_subtitle: "People with access to the platform, across every organization.",
  page_subtitle_single_org: "People with access to the platform in your organization.",
  action_refresh: "Refresh",
  action_create: "Create user",

  stat_no_roles: "people have no role assigned. They can sign in and see nothing.",
  stat_no_roles_zero: "Everyone has at least one role assigned.",

  tile_group_label: "Filter by status",
  tile_total: "Total",
  tile_active: "Active",
  tile_unverified: "Unverified",
  tile_suspended: "Suspended",
  tile_inactive: "Deactivated",
  tile_other_status: "{{count}} in another status",

  search_placeholder: "Search by name or email",
  search_min_hint: "Type at least 3 characters to search. The other filters still apply.",
  filter_role_label: "Role",
  filter_role_all: "All roles",
  filter_role_unavailable: "The role catalog could not be loaded, so filtering by role is unavailable.",

  col_person: "Person",
  col_organization: "Organization",
  col_access: "Access",
  col_status: "Status",
  col_account_access: "Account access",
  col_actions: "Actions",
  badge_you: "You",
  badge_coloca: "Coloca",
  row_actions_label: "Actions for {{name}}",

  status_active: "Active",
  status_inactive: "Deactivated",
  status_suspended: "Suspended",
  status_pending_verification: "Unverified",

  signin_suspended: "Suspended",
  signin_inactive: "Deactivated",
  signin_locked: "Locked · {{minutes}} min",
  signin_unverified: "Email not verified",
  signin_never: "Never signed in",
  signin_failing: "{{count}} failed attempts",
  signin_failing_one: "{{count}} failed attempt",
  signin_suspended_title: "Every sign-in is refused while the account is suspended.",
  signin_inactive_title: "The account is deactivated and cannot sign in.",
  signin_locked_title: "The lock lifts on its own. Nothing shortens it.",
  signin_unverified_title: "The email address is not verified.",
  signin_never_title: "This person has never signed in.",
  signin_failing_title: "Failed attempts so far. At 5 the account locks for 30 minutes.",
  signin_ok_title: "Last sign-in: {{date}}",

  org_none: "No organization",
  org_unresolved: "unresolved",
  org_internal: "internal",
  org_directory_failed: "Organization names could not be loaded. User data is unaffected.",
  org_directory_truncated:
    "There are more organizations than could be preloaded. Missing names resolve as they appear.",
  org_directory_loading: "Loading organizations…",
  org_directory_empty: "There are no organizations to choose from.",

  access_none: "No roles",
  access_unavailable: "Roles unavailable",
  access_tally: "{{allowed}}/{{total}} permissions",
  access_extra: "+{{count}}",

  action_view: "View and edit",
  action_manage_access: "Manage access",
  action_verify_email: "Verify email",
  action_activate: "Activate",
  action_deactivate: "Deactivate",
  action_suspend: "Suspend",
  action_set_password: "Set password",
  action_delete: "Delete",
  action_cancel: "Cancel",

  empty_title: "No users yet",
  empty_body: "Create the first person with access to the platform.",
  empty_filtered_title: "No user matches these filters",
  empty_filtered_body: "Adjust the search, the status or the role to see the whole population again.",
  empty_clear_filters: "Clear filters",
  error_load_failed: "The user list could not be loaded.",
  error_retry: "Try again",

  create_title: "Create user",
  create_subtitle: "Created as a member of an organization.",
  create_field_full_name: "Full name",
  create_field_email: "Email",
  create_field_organization: "Organization",
  create_field_organization_placeholder: "Search for the organization",
  create_field_language: "Language",
  create_field_country: "Country",
  create_field_phone: "Phone",
  create_field_phone_hint: "With country code and without the + sign. Example: 573101234567.",
  create_section_credentials: "Sign-in credentials",
  create_field_password: "Password",
  create_generate: "Generate",
  create_note_must_change: "They will be asked to change this password on their first sign-in.",
  create_note_no_email: "No email is sent — hand the password over through a secure channel.",
  create_submit: "Create user",
  create_success: "User created",

  detail_section_data: "Details",
  detail_section_access: "Access",
  detail_section_password: "Password",
  detail_section_activity: "Activity",
  detail_email_warning:
    "Changing the email clears its verification: the person returns to “Email not verified”.",
  detail_phone_hint: "Leave it empty to remove it.",
  detail_org_label: "Organization",
  detail_save: "Save changes",
  detail_close: "Close",
  detail_saved: "Changes saved",
  detail_alert_locked:
    "The lock lifts on its own in {{minutes}} minutes. Nothing shortens it: changing the password does not let this person in.",
  detail_alert_two_factor:
    "This account may have 2FA on with no enrolled device. Worth checking: setting a new password does not fix it.",
  detail_access_summary_none: "No roles assigned. They can sign in and see nothing.",
  detail_access_summary_unavailable:
    "This person's roles could not be read. That is not the same as holding none.",
  detail_access_summary: "{{count}} roles assigned · {{allowed}}/{{total}} effective permissions",
  detail_access_summary_one: "{{count}} role assigned · {{allowed}}/{{total}} effective permissions",
  detail_manage_access: "Manage access",

  activity_created: "Created",
  activity_updated: "Last updated",
  activity_last_login: "Last sign-in",
  activity_password_changed: "Password changed",
  activity_email_verified: "Email verified",
  activity_failed_attempts: "Failed attempts",
  activity_locked_until: "Locked until",
  activity_two_factor_flag: "2FA (flag)",
  activity_never: "Never",
  activity_yes: "Yes",
  activity_no: "No",

  password_new: "New password",
  password_confirm: "Confirm",
  password_requirements:
    "Between 8 and 128 characters, with an uppercase, a lowercase, a number and a symbol.",
  password_mismatch: "The passwords do not match.",
  password_note_sessions: "This does not sign the person out of their open sessions.",
  password_note_no_force: "They will not be asked to change it on their next sign-in.",
  password_note_no_email: "No email is sent.",
  password_note_compromised: "If the account is compromised: suspend first, then change the password.",
  password_submit: "Set password",
  password_success: "Password updated",

  suspend_title: "Suspend {{name}}?",
  suspend_body: "Sessions already open are not closed, but every future sign-in will be refused.",
  suspend_confirm: "Suspend",
  delete_title: "Delete {{name}}?",
  delete_body: "This is reversible only by engineering, and every session of theirs closes immediately.",
  delete_email_note: "Their email stays taken: you will not be able to create another person with it.",
  delete_confirm: "Delete",
  delete_success: "User deleted",

  verify_email_success: "Email verified",
  status_change_success: "Status updated",
  error_email_taken: "A user with this email already exists.",
  error_email_deleted_reuse:
    "Could not create. If this email belonged to a deleted user it cannot be reused — try another one.",
  validation_required: "This field is required.",
  validation_email: "Enter a valid email address.",
};
